import React, { useState, ChangeEvent } from 'react'
import { gql, useQuery } from '@apollo/client'
import {
  Row, Col, Input, Label,
} from 'reactstrap'
import Loader from 'components/common/Loader'
import { Query } from 'generated/types'
import PrimaryButton from 'components/common/PrimaryButton'
import Divider from 'components/common/Divider'
import SearchBar from 'components/common/SearchBar'
import { createSpaceRoute, spaceRoute } from 'helpers/routing'
import { Link } from 'react-router-dom'
import LayoutTitle from 'teleporters/LayoutTitle'
import PageTitle from 'components/common/PageTitle'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Table } from 'components/common/Table'
import { useFeatures } from 'components/FeaturesContext'
import InfoBlock from 'components/common/InfoBlock'

export const SPACES_QUERY = gql`
    query Spaces($where: QuerySpacesWhereInput = { active: { equals: true } } ) {
        spaces(where: $where) {
            id
            name
            location {
                text
            }
        }
    }
`

export const SpacesPage: React.FC = () => {
  const features = useFeatures()
  const [filter, setFilter] = useState('')
  const activeOnlyVariables = { where: { active: { equals: true } } }
  const { data, loading, refetch } = useQuery<Query>(SPACES_QUERY, { variables: activeOnlyVariables })

  const toggleActiveOnly = (bool: boolean) => {
    if (bool) {
      refetch(activeOnlyVariables)
    } else {
      refetch({ where: { } })
    }
  }

  if (loading) {
    return (
      <Loader title="Loading your spaces..." />
    )
  }

  const rows = data?.spaces.filter((space) => {
    if (filter === '') {
      return true
    }
    return space.name.toLowerCase().includes(filter.toLocaleLowerCase())
  }).sort((a, b) => {
    if (a.location.text !== b.location.text) {
      return (a.location.text || '').toLowerCase() > (b.location.text || '').toLowerCase() ? 1 : -1
    }
    return a.name > b.name ? 1 : -1
  }).map((space) => (
    <tr key={`space-${space.id}`}>
      <td><Link to={spaceRoute(space.id)}>{space.name}</Link></td>
      <td>{space.location.text}</td>
    </tr>
  ))

  let showAddSpace = true
  if (features.isFreemium && Number(data?.spaces.length) > 1) {
    showAddSpace = false
  }

  return (
    <>
      <LayoutTitle.Source>
        <PageTitle>
          Spaces
        </PageTitle>
      </LayoutTitle.Source>
      <Row>
        <Col xs="12" sm="12" md="12" lg="3">
          { showAddSpace
            && (
            <PrimaryButton
              title="Add Space"
              icon="plus"
              to={createSpaceRoute()}
            />
            )}
          { !showAddSpace
            && (
            <Row>
              <Col>
                <InfoBlock
                  title="Need more spaces?"
                  subtitle={(
                    <a
                      href="mailto:info@quorumcontrol.com?subject=BirdLevel%20Upgrade"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Contact Us
                    </a>
                  )}
                />
              </Col>
            </Row>
            )}
        </Col>
      </Row>

      <Divider />

      <Row>
        <Col xs="12" sm="12" md="10" lg="4">
          <SearchBar
            value={filter}
            onChange={(evt: ChangeEvent<HTMLInputElement>) => { setFilter(evt.target.value) }}
          />
        </Col>
        <Col xs="12" sm="12" md="2" lg="4">
          <Label check for="showArchivedToggle" style={{ marginLeft: '1.25rem', marginTop: '10px' }}>
            <Input
              type="checkbox"
              id="showArchivedToggle"
              onChange={(evt) => { toggleActiveOnly(!evt.target.checked) }}
            />
            Show Archived Spaces
          </Label>
        </Col>
      </Row>

      <Row className="pt-4">
        <Col xs="12" sm="12" md="12" lg="6">
          <Table>
            <thead>
              <tr>
                <th><FontAwesomeIcon icon="map-marker-alt" className="mr-1" /> Space</th>
                <th><FontAwesomeIcon icon="map" className="mr-1" /> Location</th>
              </tr>
            </thead>
            <tbody>
              {rows}
            </tbody>
          </Table>
        </Col>
      </Row>

    </>
  )
}

export default SpacesPage
